import { useEffect, useMemo, useState } from "react";
import { RefreshCw, TicketPercent, TimerReset, WalletCards, X } from "lucide-react";
import { showToastMessage } from "utils";
import StatusBadge from "../components/StatusBadge";
import { getVoucherDetailV2, getVouchersV2 } from "./api";

function formatDays(value) {
  if (value == null) return "-";
  if (value < 0) return `${Math.abs(value)} overdue`;
  if (value === 0) return "Today";
  return `${value} days`;
}

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleDateString();
}

function VoucherDetailDrawer({ open, detail, loading, onClose }) {
  if (!open) return null;
  const overview = detail?.overview;

  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-justify-end">
      <button type="button" className="tw-absolute tw-inset-0 tw-bg-[rgba(20,26,44,0.28)]" onClick={onClose} />
      <aside className="admin-v2-soft-panel tw-relative tw-z-[1] tw-h-full tw-w-full tw-max-w-[680px] tw-overflow-y-auto tw-rounded-none tw-border-l tw-border-[rgba(31,41,64,0.08)] tw-p-4 sm:tw-p-5 lg:tw-p-6">
        <div className="tw-mb-5 tw-flex tw-items-start tw-justify-between tw-gap-4">
          <div>
            <p className="admin-v2-eyebrow">Voucher detail</p>
            <h2 className="tw-mt-2 tw-text-xl tw-font-semibold tw-text-[var(--admin-v2-ink)] sm:tw-text-2xl">{overview?.name || "Loading voucher"}</h2>
            <p className="tw-mt-1 tw-text-sm admin-v2-text-muted">{overview?.code || "-"}</p>
          </div>
          <button type="button" onClick={onClose} className="tw-inline-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-text-[var(--admin-v2-ink)]"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="tw-p-4 admin-v2-text-muted">Loading voucher detail...</div>
        ) : overview ? (
          <div className="tw-space-y-5">
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
              <div>
                <p className="admin-v2-eyebrow">Campaign overview</p>
                <p className="tw-mt-2 tw-text-2xl tw-font-semibold tw-text-[var(--admin-v2-ink)]">{overview.percent}% discount</p>
                <p className="tw-mt-1 tw-text-sm admin-v2-text-muted">{overview.private ? "Private voucher" : "Public voucher"}</p>
              </div>
              <StatusBadge status={overview.status} />
            </div>

            <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
              <div className="tw-rounded-[20px] tw-bg-[rgba(44,50,85,0.04)] tw-p-4"><p className="admin-v2-eyebrow tw-mb-2">Validity</p><p className="tw-text-lg tw-font-semibold tw-text-[var(--admin-v2-ink)]">{formatDays(overview.daysLeft)}</p><p className="tw-mt-1 tw-text-sm admin-v2-text-muted">{formatDate(overview.endsAt)}</p></div>
              <div className="tw-rounded-[20px] tw-bg-[rgba(250,216,69,0.14)] tw-p-4"><p className="admin-v2-eyebrow tw-mb-2">Usage</p><p className="tw-text-lg tw-font-semibold tw-text-[var(--admin-v2-ink)]">{overview.remaining} left</p><p className="tw-mt-1 tw-text-sm admin-v2-text-muted">{overview.used} used / {overview.limit} limit</p></div>
            </div>

            <div className="tw-grid tw-gap-3 sm:tw-grid-cols-3">
              <div className="tw-rounded-[18px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-p-4"><p className="admin-v2-eyebrow tw-mb-1">Allowed users</p><p className="tw-text-xl tw-font-semibold tw-text-[var(--admin-v2-ink)]">{overview.allowedUsersCount || 0}</p></div>
              <div className="tw-rounded-[18px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-p-4"><p className="admin-v2-eyebrow tw-mb-1">Redeemed</p><p className="tw-text-xl tw-font-semibold tw-text-[var(--admin-v2-ink)]">{overview.used || 0}</p></div>
              <div className="tw-rounded-[18px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-p-4"><p className="admin-v2-eyebrow tw-mb-1">Starts</p><p className="tw-text-base tw-font-semibold tw-text-[var(--admin-v2-ink)]">{formatDate(overview.startsAt)}</p></div>
            </div>

            <section className="tw-rounded-[24px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-p-4">
              <p className="tw-text-sm tw-font-semibold tw-text-[var(--admin-v2-ink)]">Allowed users</p>
              <div className="tw-mt-4 tw-space-y-2">
                {(detail.allowedUsers || []).length ? detail.allowedUsers.map((item, index) => (
                  <div key={`${item}-${index}`} className="tw-rounded-2xl tw-bg-[rgba(44,50,85,0.04)] tw-px-3 tw-py-2.5 tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">{String(item)}</div>
                )) : <div className="tw-rounded-2xl tw-bg-[rgba(44,50,85,0.04)] tw-px-3 tw-py-3 tw-text-sm admin-v2-text-muted">No restricted user list attached.</div>}
              </div>
            </section>

            <section className="tw-rounded-[24px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-p-4">
              <p className="tw-text-sm tw-font-semibold tw-text-[var(--admin-v2-ink)]">Redeemed entries</p>
              <div className="tw-mt-4 tw-space-y-2">
                {(detail.redeemedUsers || []).length ? detail.redeemedUsers.map((item, index) => (
                  <div key={`${item}-${index}`} className="tw-rounded-2xl tw-bg-[rgba(44,50,85,0.04)] tw-px-3 tw-py-2.5 tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">{String(item)}</div>
                )) : <div className="tw-rounded-2xl tw-bg-[rgba(44,50,85,0.04)] tw-px-3 tw-py-3 tw-text-sm admin-v2-text-muted">No redemption data yet.</div>}
              </div>
            </section>
          </div>
        ) : null}
      </aside>
    </div>
  );
}

export default function VouchersPage() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState(null);

  const loadRows = async (mode = "initial") => {
    try {
      mode === "initial" ? setLoading(true) : setRefreshing(true);
      const data = await getVouchersV2();
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      showToastMessage(error?.message || "Failed to load vouchers");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    const loadDetail = async () => {
      try {
        setDetailLoading(true);
        const data = await getVoucherDetailV2(selectedId);
        if (!cancelled) setDetail(data);
      } catch (error) {
        if (!cancelled) showToastMessage(error?.message || "Failed to load voucher detail");
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    loadDetail();
    return () => { cancelled = true; };
  }, [selectedId]);

  const filteredRows = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesQuery =
        !normalizedQuery ||
        [row.name, row.code, row.accountType]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedQuery));
      const matchesStatus = statusFilter === "all" || String(row.status || "").toLowerCase() === statusFilter;
      return matchesQuery && matchesStatus;
    });
  }, [query, rows, statusFilter]);

  const summary = useMemo(() => [
    { label: "Vouchers", value: rows.length, note: "Promotion records available", icon: <WalletCards size={18} /> },
    { label: "Active", value: rows.filter((row) => row.status === "Active").length, note: "Currently usable codes", icon: <TicketPercent size={18} /> },
    { label: "Ending soon", value: rows.filter((row) => row.status === "Ending soon").length, note: "Expiring inside 7 days", icon: <TimerReset size={18} /> },
    { label: "Redeemed", value: rows.reduce((sum, row) => sum + Number(row.used || 0), 0), note: "Total voucher redemptions", icon: <WalletCards size={18} /> },
  ], [rows]);

  return (
    <div className="tw-space-y-6">
      <section className="admin-v2-panel tw-p-5 sm:tw-p-6 lg:tw-p-8">
        <div className="tw-space-y-3">
          <p className="admin-v2-eyebrow">Vouchers</p>
          <h1 className="admin-v2-title">Voucher operations with dedicated v2 data, cleaner inspection, and side-panel detail.</h1>
          <p className="admin-v2-subtitle">Click any voucher row to inspect validity, usage, allowed users, and redeemed entries.</p>
        </div>
      </section>

      <section className="admin-v2-soft-panel tw-p-4 lg:tw-p-6">
        <div className="tw-grid tw-gap-4 md:tw-grid-cols-2 xl:tw-grid-cols-4">
          {summary.map((item) => (
            <div key={item.label} className="admin-v2-stat-panel">
              <div className="tw-flex tw-items-center tw-justify-between"><p className="admin-v2-eyebrow">{item.label}</p><span className="tw-text-[var(--admin-v2-accent)]">{item.icon}</span></div>
              <p className="tw-mt-5 tw-text-4xl tw-font-semibold tw-text-[var(--admin-v2-ink)]">{item.value}</p>
              <p className="tw-mt-2 tw-text-sm admin-v2-text-muted">{item.note}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="admin-v2-soft-panel tw-p-4 lg:tw-p-6">
        <div className="tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row lg:tw-items-center lg:tw-justify-between">
          <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-3 sm:tw-flex-row">
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search voucher name, code, or scope..." className="tw-w-full tw-rounded-2xl tw-border tw-border-[rgba(31,41,64,0.12)] tw-bg-white tw-px-4 tw-py-3 tw-text-sm tw-text-[var(--admin-v2-ink)] sm:tw-max-w-[420px]" />
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="tw-rounded-2xl tw-border tw-border-[rgba(31,41,64,0.12)] tw-bg-white tw-px-4 tw-py-3 tw-text-sm tw-text-[var(--admin-v2-ink)]">
              <option value="all">All voucher states</option>
              <option value="active">Active</option>
              <option value="ending soon">Ending soon</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          <button type="button" className="admin-v2-action admin-v2-action-secondary" onClick={() => loadRows("refresh")}>
            <RefreshCw size={16} className={refreshing ? "tw-animate-spin" : ""} />
            Refresh
          </button>
        </div>

        <div className="tw-mt-5">
          {loading ? (
            <div className="tw-p-8 admin-v2-text-muted">Loading vouchers...</div>
          ) : !filteredRows.length ? (
            <div className="tw-p-8 admin-v2-text-muted">No vouchers match the current filters.</div>
          ) : (
            <div className="admin-v2-table-desktop tw-overflow-x-auto">
              <table className="admin-v2-data-table">
                <thead>
                  <tr>
                    <th>Voucher</th>
                    <th>Scope</th>
                    <th>Discount</th>
                    <th>Usage</th>
                    <th>Validity</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr key={row.id} className="tw-cursor-pointer" onClick={() => { setSelectedId(row.id); setDetailOpen(true); }}>
                      <td><div className="tw-space-y-1"><p className="tw-font-semibold tw-text-[var(--admin-v2-ink)]">{row.name}</p><p className="tw-text-sm admin-v2-text-muted">{row.code}</p></div></td>
                      <td><span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-[rgba(44,50,85,0.05)] tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-medium tw-text-[var(--admin-v2-muted)]">{row.private ? "Private" : "Public"} / {row.accountType}</span></td>
                      <td><p className="tw-font-semibold tw-text-[var(--admin-v2-ink)]">{row.percent}% off</p></td>
                      <td><div className="tw-min-w-[180px] tw-space-y-2"><div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-text-xs"><span className="admin-v2-text-muted">{row.used} redeemed</span><span className="tw-font-semibold tw-text-[var(--admin-v2-ink)]">{row.remaining} left</span></div><div className="admin-v2-progress"><span style={{ width: `${row.limit > 0 ? Math.min((row.used / row.limit) * 100, 100) : 0}%` }} /></div><p className="tw-text-xs admin-v2-text-muted">{row.limit} total limit</p></div></td>
                      <td><div className="tw-space-y-1"><p className="tw-font-medium tw-text-[var(--admin-v2-ink)]">{formatDays(row.daysLeft)}</p><p className="tw-text-sm admin-v2-text-muted">{formatDate(row.endsAt)}</p></div></td>
                      <td><StatusBadge status={row.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <VoucherDetailDrawer open={detailOpen} detail={detail} loading={detailLoading} onClose={() => setDetailOpen(false)} />
    </div>
  );
}
